import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router";
import ExplorerGraph from "./ExplorerGraph";
import ExplorerDetailPanel from "./ExplorerDetailPanel";
import { useSalesOrderGraph } from "./graphs/useSalesOrderGraph";
import { useServiceOrderGraph } from "./graphs/useServiceOrderGraph";
import { useManufacturingGraph } from "./graphs/useManufacturingGraph";
import { useGetSalesOrderLineComparison } from "../hooks/SalesOrderLine/useGetSalesOrderLineComparison";
import { useGetServiceOrderLineComparison } from "../hooks/ServiceOrder/useGetServiceOrderLineComparison";
import { useExplorerStore } from "../stores/useExplorerStore";
import { useLayoutContext } from "@ntg/component-library/contexts";
import { useDetailPanelContent } from "./hooks/useDetailPanelContent";
import type {
    ExplorerEntityType,
    ExplorerEntryType,
    ExplorerNode,
} from "../declarations/IExplorerTypes";
import type { IComparisonInfo } from "../declarations/types";
import type { IReadVerbPayload } from "../declarations/IReadVerbPayload";
import type { IBomLineCS } from "../hooks/Route/useGetBomLinesCS";
import type { IProductD365 } from "../hooks/Product/useGetProductD365";
import type { IReleasedProductD365 } from "../hooks/ReleasedProduct/useGetReleasedProductD365";
import type { ILineExistenceInfo } from "../hooks/SalesOrderLine/useGetSalesOrderLineExistence";
import type { Edge, Node, OnNodesChange } from "@xyflow/react";

interface IExplorerViewConfig {
    /** Store entryType this view binds to. Used to gate stale selection from other views. */
    entryType: ExplorerEntryType;
    /** Human label for the loading state. */
    displayName: string;
    /** Node-id prefix that identifies a "line detail" node for this view. */
    lineDetailPrefix: string;
    /** Entity type to surface when a line detail node is selected. */
    lineDetailEntityType: ExplorerEntityType;
    /** Node id of the lines-list node (selecting it shows existence table); null if this view has no list. */
    linesListId: string | null;
    /** Ordered prefix → entity-type mapping used to resolve the panel's selectedEntityType. */
    prefixMap: ReadonlyArray<readonly [string, ExplorerEntityType]>;
}

const FULL_PREFIX_MAP: ReadonlyArray<readonly [string, ExplorerEntityType]> = [
    ["ReleasedProduct-", "ReleasedProduct"],
    ["ReleasedProducts-", "ReleasedProduct"],
    ["Product-", "Product"],
    ["Routes-", "Route"],
    ["OpDetail-", "RouteOperationDetail"],
    ["BomLines-", "BomLines"],
    ["FMRoute-", "FMRoute"],
    ["ProductionOrder-", "ProductionOrder"],
];

const SERVICE_ORDER_PREFIX_MAP: ReadonlyArray<readonly [string, ExplorerEntityType]> = [
    ["ReleasedProduct-", "ReleasedProduct"],
    ["ReleasedProducts-", "ReleasedProduct"],
    ["Product-", "Product"],
];

const SALES_ORDER_CONFIG: IExplorerViewConfig = {
    entryType: "SalesOrder",
    displayName: "Sales Order",
    lineDetailPrefix: "SalesOrderLineDetail-",
    lineDetailEntityType: "SalesOrderLineDetail",
    linesListId: "SalesOrderLines",
    prefixMap: FULL_PREFIX_MAP,
};

const SERVICE_ORDER_CONFIG: IExplorerViewConfig = {
    entryType: "ServiceOrder",
    displayName: "Service Order",
    lineDetailPrefix: "ServiceOrderLineDetail-",
    lineDetailEntityType: "ServiceOrderLineDetail",
    linesListId: "ServiceOrderLines",
    prefixMap: SERVICE_ORDER_PREFIX_MAP,
};

const MANUFACTURING_CONFIG: IExplorerViewConfig = {
    entryType: "Manufacturing",
    displayName: "Manufacturing Item",
    // Manufacturing reuses the sales-order line detail node id format.
    lineDetailPrefix: "SalesOrderLineDetail-",
    lineDetailEntityType: "SalesOrderLineDetail",
    linesListId: null,
    prefixMap: FULL_PREFIX_MAP,
};

interface ILineComparisonResult {
    data: IComparisonInfo[] | undefined;
    isLoading: boolean;
    error: Error | null;
}

interface IExplorerShellProps {
    bomLineMap?: Map<string, IBomLineCS[]>;
    bomRPComparisonMap?: Map<string, { data?: IComparisonInfo[]; isLoading: boolean }>;
    buyProductMap?: Map<string, { data?: IProductD365; isLoading: boolean; isError: boolean }>;
    buyRPMap?: Map<string, { data?: IReleasedProductD365; isLoading: boolean; isError: boolean }>;
    comparisonDataMap: Record<string, IComparisonInfo[] | undefined>;
    config: IExplorerViewConfig;
    edges: Edge[];
    existenceData: ILineExistenceInfo[] | undefined;
    isInitLoading: boolean;
    lineComparison: ILineComparisonResult;
    nodes: Node<ExplorerNode["data"], string | undefined>[];
    onNodesChange: OnNodesChange;
    orderID: number;
    routeComparisonMap?: Map<string, { headerData?: IComparisonInfo[]; versionData?: IComparisonInfo[]; isLoading: boolean }>;
}

function resolveSelectedEntityType(
    selectedNodeId: string | null,
    isLineDetailSelected: boolean,
    config: IExplorerViewConfig,
): ExplorerEntityType | null {
    if (isLineDetailSelected) {
        return config.lineDetailEntityType;
    }
    if (!selectedNodeId) {
        return null;
    }
    for (const [prefix, entityType] of config.prefixMap) {
        if (selectedNodeId.startsWith(prefix)) {
            return entityType;
        }
    }
    // Fallback: bare-name node ids (e.g. "Customer", "VatNum") align with ExplorerEntityType values.
    return selectedNodeId as ExplorerEntityType;
}

function ExplorerShell({
    bomLineMap,
    bomRPComparisonMap,
    buyProductMap,
    buyRPMap,
    comparisonDataMap,
    config,
    edges,
    existenceData,
    isInitLoading,
    lineComparison,
    nodes,
    onNodesChange,
    orderID,
    routeComparisonMap,
}: IExplorerShellProps) {
    // Gate against stale selection from a previous view: setEntry() resets selectedNodeId
    // in a useEffect that fires after this render, so we ignore the store value until the
    // store's entryType/orderID match this view.
    const selectedNodeId = useExplorerStore((s) =>
        s.entryType === config.entryType && s.orderID === orderID ? s.selectedNodeId : null,
    );
    const setSelectedNodeId = useExplorerStore((s) => s.setSelectedNodeId);

    const isLineDetailSelected = selectedNodeId?.startsWith(config.lineDetailPrefix) ?? false;
    const isLinesListSelected = config.linesListId !== null && selectedNodeId === config.linesListId;

    const selectedEntityType = resolveSelectedEntityType(selectedNodeId, isLineDetailSelected, config);

    const detailPanelContent = useDetailPanelContent({
        bomLineMap,
        bomRPComparisonMap,
        buyProductMap,
        buyRPMap,
        comparisonDataMap,
        existenceData,
        isLineDetailSelected,
        isLinesListSelected,
        lineComparison,
        nodes,
        routeComparisonMap,
        selectedEntityType,
        selectedNodeId,
    });

    // Dismiss detail panel on Escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && selectedNodeId) {
                setSelectedNodeId(null);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedNodeId, setSelectedNodeId]);

    if (isInitLoading) {
        return (
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                }}
            >
                <CircularProgress />
                <Typography>Loading {config.displayName}...</Typography>
            </Box>
        );
    }

    const showDetailPanel = selectedNodeId !== null;

    return (
        <Box
            sx={{
                height: "calc(100vh - 120px)",
                display: "flex",
                gap: 1,
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    flex: showDetailPanel ? "0 0 60%" : "1 1 100%",
                    transition: "flex 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    background: (t) => t.palette.mode === "dark"
                        ? "rgba(18, 18, 24, 0.6)"
                        : "rgba(248, 250, 253, 0.8)",
                }}
            >
                <ExplorerGraph nodes={nodes} edges={edges} onNodesChange={onNodesChange} />
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    flex: showDetailPanel ? "0 0 39%" : "0 0 0%",
                    opacity: showDetailPanel ? 1 : 0,
                    borderRadius: 3,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    border: showDetailPanel ? "1px solid" : "0px solid",
                    borderColor: "divider",
                    backdropFilter: "blur(8px)",
                    transition: "flex 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease 0.05s",
                    pointerEvents: showDetailPanel ? "auto" : "none",
                }}
            >
                {showDetailPanel && (
                    <ExplorerDetailPanel
                        selectedEntityType={selectedEntityType}
                        content={detailPanelContent}
                        onClose={() => setSelectedNodeId(null)}
                    />
                )}
            </Paper>
        </Box>
    );
}

export default function DataExplorerPage() {
    const { entryType, id } = useParams<{ entryType: string; id: string }>();
    const { SetPrimaryTitle, SetSecondaryHeader } = useLayoutContext();

    const setEntry = useExplorerStore((s) => s.setEntry);

    const orderID = Number(id) || 0;
    const validEntryType = (entryType as ExplorerEntryType) ?? "SalesOrder";

    useEffect(() => {
        if (orderID > 0) {
            setEntry(validEntryType, orderID);
        }
    }, [validEntryType, orderID, setEntry]);

    useEffect(() => {
        SetPrimaryTitle("Data Explorer");
        SetSecondaryHeader(null);
        return () => {
            SetPrimaryTitle("");
            SetSecondaryHeader(null);
        };
    }, [SetPrimaryTitle, SetSecondaryHeader]);

    if (!entryType || !id || orderID <= 0) {
        return (
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography variant="h6" sx={{ color: "text.secondary" }}>
                    Navigate to the Explorer from a Sales Order, Service Order, or
                    Manufacturing Order.
                </Typography>
            </Box>
        );
    }

    if (validEntryType === "SalesOrder") {
        return <SalesOrderExplorer orderID={orderID} />;
    }

    if (validEntryType === "ServiceOrder") {
        return <ServiceOrderExplorer orderID={orderID} />;
    }

    if (validEntryType === "Manufacturing") {
        return <ManufacturingExplorer orderItemID={orderID} />;
    }

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Typography variant="h6" sx={{ color: "text.secondary" }}>
                {validEntryType} explorer is coming soon.
            </Typography>
        </Box>
    );
}

function SalesOrderExplorer({ orderID }: { orderID: number }) {
    const resolvedKeys = useExplorerStore((s) => s.resolvedKeys);
    const selectedNodeId = useExplorerStore((s) =>
        s.entryType === "SalesOrder" && s.orderID === orderID ? s.selectedNodeId : null,
    );

    const graph = useSalesOrderGraph(orderID);

    // Resolve the selected line node's lot id so the line-comparison hook can fetch when expanded.
    const isLineDetailSelected = selectedNodeId?.startsWith(SALES_ORDER_CONFIG.lineDetailPrefix) ?? false;
    const selectedLineNode = isLineDetailSelected
        ? graph.nodes.find((n) => n.id === selectedNodeId)
        : undefined;
    const selectedLineLotId = selectedLineNode?.data?.lineDetail?.inventoryLotId ?? "";

    const lineComparisonPayload: IReadVerbPayload = useMemo(() => ({
        keys: {
            dataAreaId: resolvedKeys?.dataAreaId ?? "",
            inventoryLotId: selectedLineLotId,
            orderID: orderID,
        },
    }), [resolvedKeys?.dataAreaId, selectedLineLotId, orderID]);

    const lineComparison = useGetSalesOrderLineComparison(lineComparisonPayload);

    return (
        <ExplorerShell
            config={SALES_ORDER_CONFIG}
            orderID={orderID}
            nodes={graph.nodes}
            edges={graph.edges}
            onNodesChange={graph.onNodesChange}
            isInitLoading={graph.isInitLoading}
            comparisonDataMap={graph.comparisonDataMap as Record<string, IComparisonInfo[] | undefined>}
            existenceData={graph.existenceData}
            routeComparisonMap={graph.routeComparisonMap}
            bomRPComparisonMap={graph.bomRPComparisonMap}
            bomLineMap={graph.bomLineMap}
            buyProductMap={graph.buyProductMap}
            buyRPMap={graph.buyRPMap}
            lineComparison={lineComparison}
        />
    );
}

function ServiceOrderExplorer({ orderID }: { orderID: number }) {
    const resolvedKeys = useExplorerStore((s) => s.resolvedKeys);
    const selectedNodeId = useExplorerStore((s) =>
        s.entryType === "ServiceOrder" && s.orderID === orderID ? s.selectedNodeId : null,
    );

    const graph = useServiceOrderGraph(orderID);

    const isLineDetailSelected = selectedNodeId?.startsWith(SERVICE_ORDER_CONFIG.lineDetailPrefix) ?? false;
    const selectedLineNode = isLineDetailSelected
        ? graph.nodes.find((n) => n.id === selectedNodeId)
        : undefined;
    const selectedLineLotId = selectedLineNode?.data?.lineDetail?.inventoryLotId ?? "";
    const selectedLineNumber = selectedLineNode?.data?.lineDetail?.lineNumber ?? 0;

    const lineComparisonPayload: IReadVerbPayload = useMemo(() => ({
        keys: {
            dataAreaId: resolvedKeys?.dataAreaId ?? "",
            inventoryLotId: selectedLineLotId,
            lineNumber: selectedLineNumber,
            serviceOrderID: orderID,
        },
    }), [resolvedKeys?.dataAreaId, selectedLineLotId, selectedLineNumber, orderID]);

    const lineComparison = useGetServiceOrderLineComparison(lineComparisonPayload);

    return (
        <ExplorerShell
            config={SERVICE_ORDER_CONFIG}
            orderID={orderID}
            nodes={graph.nodes}
            edges={graph.edges}
            onNodesChange={graph.onNodesChange}
            isInitLoading={graph.isInitLoading}
            comparisonDataMap={graph.comparisonDataMap as Record<string, IComparisonInfo[] | undefined>}
            existenceData={graph.existenceData}
            lineComparison={lineComparison}
        />
    );
}

function ManufacturingExplorer({ orderItemID }: { orderItemID: number }) {
    const graph = useManufacturingGraph(orderItemID);

    // Manufacturing's line-comparison payload comes from the graph's `init`, not from a selected node.
    const lineComparisonPayload: IReadVerbPayload = useMemo(() => ({
        keys: {
            dataAreaId: graph.init?.dataAreaId ?? "",
            inventoryLotId: graph.init?.inventoryLotId ?? "",
            orderID: graph.init?.orderID ?? 0,
        },
    }), [graph.init?.dataAreaId, graph.init?.inventoryLotId, graph.init?.orderID]);

    const lineComparison = useGetSalesOrderLineComparison(lineComparisonPayload);

    return (
        <ExplorerShell
            config={MANUFACTURING_CONFIG}
            orderID={orderItemID}
            nodes={graph.nodes}
            edges={graph.edges}
            onNodesChange={graph.onNodesChange}
            isInitLoading={graph.isInitLoading}
            comparisonDataMap={graph.comparisonDataMap as Record<string, IComparisonInfo[] | undefined>}
            existenceData={graph.existenceData}
            routeComparisonMap={graph.routeComparisonMap}
            bomRPComparisonMap={graph.bomRPComparisonMap}
            bomLineMap={graph.bomLineMap}
            buyProductMap={graph.buyProductMap}
            buyRPMap={graph.buyRPMap}
            lineComparison={lineComparison}
        />
    );
}
